// University of Illinois Urbana-Champaign: public college page (#2652),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Illinois' Common Data Set 2025-2026, an Excel file (Box, uofi.box.com)
//    linked from the Division of Analytics and Institutional Research's
//    Common Data Set page; the SOURCES url is that illinois.edu page. C1
//    applicants, admits and enrollees for Fall 2025 (totals 83,045 / 30,384 /
//    9,207, each the sum of the male, female and unknown rows); C8 test
//    policy; C21/C22 plans. Quirk: the C1 residency table in the file is
//    broken (two applied cells read "#REF!" and in-state admits, 32,702, are
//    above in-state applicants, 29,419), so no residency rows are shown.
//    The file's G1/G5 costs are for 2025-2026, so they are not used.
//  - admissions.illinois.edu: First-Year Admit Rates ("Admit Rates: 2026
//    First-Year", by college, first-choice majors only), First-Year Dates &
//    Deadlines, How to Apply: First-Year (test policy, portfolio, audition
//    and interview steps) and First-Year Requirements (the 3.5 years of math
//    some majors require). The dates page carries no year; it is the page
//    for the application that opened September 1, 2026, and the How to
//    Apply page names fall 2027 (its English-test note). No change for fall
//    2028 entry was announced on these pages.
//  - Office of Student Financial Aid, Undergraduate Cost: the cost of
//    attendance for students who enroll in fall 2026 (cost.illinois.edu
//    estimator, base rate, yearly view, Illinois resident and non-Illinois
//    U.S. resident). Rates for students starting in fall 2027 are not posted
//    yet. Tuition is higher in some colleges; the note gives the Gies and
//    Grainger rates from the same estimator.
//  - College Scorecard, school id 145637 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Illinois Common Data Set 2025-2026',
        url: 'https://dair.illinois.edu/access-data/common-data-set/',
        read: '2026-10-01',
    },
    admitRates: {
        label: 'Illinois Undergraduate Admissions, First-Year Admit Rates',
        url: 'https://admissions.illinois.edu/admit-rates/',
        read: '2026-10-01',
    },
    dates: {
        label: 'Illinois Undergraduate Admissions, First-Year Dates & Deadlines',
        url: 'https://admissions.illinois.edu/first-year-dates/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Illinois Undergraduate Admissions, How to Apply: First-Year',
        url: 'https://admissions.illinois.edu/first-year-apply/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Illinois Undergraduate Admissions, First-Year Requirements',
        url: 'https://admissions.illinois.edu/first-year-requirements/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Illinois Office of Student Financial Aid, Undergraduate Cost',
        url: 'https://www.osfa.illinois.edu/cost/undergraduate-cost/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?145637-University-of-Illinois-Urbana-Champaign',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-illinois-urbana-champaign',
    kbId: 'university_of_illinois_urbana_champaign',
    name: 'University of Illinois Urbana-Champaign',
    shortName: 'Illinois',
    aliases: ['UIUC', 'U of I'],
    location: 'Urbana-Champaign, IL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Illinois brand colors: Illini Orange,
    // Illini Blue); decoration only.
    branding: {
        primary: '#FF5F05',
        secondary: '#13294B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_illinois_urbana_champaign_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 83045,
        admitted: 30384,
        enrolled: 9207,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        year: 'Fall 2026',
        published: true,
        note: 'Illinois admits by major and publishes first-year admit rates by college for fall 2026. These count first-choice majors only. For the same class it gives a total admit rate of 38.1% and a first-choice major admit rate of 32.8%. The rate above is from the Common Data Set for fall 2025, a year earlier.',
        head: ['College', 'Rate'],
        rows: [
            { name: 'College of Agricultural, Consumer & Environmental Sciences', rate: 46.7 },
            { name: 'College of Applied Health Sciences', rate: 28.9 },
            { name: 'College of Education', rate: 42.5 },
            { name: 'College of Fine & Applied Arts', rate: 55.5 },
            { name: 'College of Liberal Arts & Sciences', rate: 40.6 },
            { name: 'College of Media', rate: 48.2 },
            { name: 'Division of Exploratory Studies', rate: 54.3 },
            { name: 'Gies College of Business', rate: 22.5 },
            { name: 'Grainger College of Engineering', rate: 23.3 },
            { name: 'School of Information Sciences', rate: 52.8 },
            { name: 'School of Social Work', rate: 38.8 },
        ],
        source: 'admitRates',
        where: 'Admit Rates: 2026 First-Year',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Illinois has no early decision plan. Its early action plan is not binding and not restrictive, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to a first-choice major, with an optional second choice, and your fit with that major is part of the review. Some majors ask for more:',
        rows: [
            { school: 'Art and design', requirement: 'You must submit a portfolio.', source: 'apply' },
            { school: 'Dance', requirement: 'You must complete an audition.', source: 'apply' },
            { school: 'Music', requirement: 'You must submit a separate music application and complete an audition or interview, depending on your program.', source: 'apply' },
            { school: 'Theatre', requirement: 'You must complete an interview and either an audition or a portfolio review, depending on your program.', source: 'apply' },
            { school: 'Engineering, business, computer science and some sciences', requirement: 'At least 3.5 years of high school math, including trigonometry or higher, for every Grainger Engineering, Gies Business and Computer Science + X major, and for a listed set of others such as Chemistry, Astrophysics, Architectural Studies and Information Sciences. Other majors need 3 or 3.5 years.', source: 'requirements' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular deadline', value: 'January 5' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'January 29', binding: false },
            { plan: 'Regular', deadline: 'January 5', decision: 'February 26', binding: false },
        ],
        note: 'The application opens September 1, through the Common App. Required materials are due November 7 for Early Action and January 11 for Regular, by 11:59 p.m. Central. Honors and scholarship news comes by April 1, and the commit deadline is May 1.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Illinois is test-optional: you choose whether to include ACT or SAT scores. If you do, report your highest ACT composite or highest SAT total (Illinois does not superscore) and your highest section scores, even from different dates. The ACT writing test is not used. Official scores are needed only if you enroll.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [13252, 35928] },
            { item: 'Required fees', amounts: [5182, 5182] },
            { item: 'Food and housing', amounts: [15858, 15858] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Other expenses', amounts: [2500, 2840] },
        ],
        totals: [37992, 61008],
        note: 'The base rate for a full-time undergraduate who starts in fall 2026, for fall and spring; rates for fall 2027 starters are not posted yet. Tuition is higher in some programs: in Gies Business and Grainger Engineering it is $18,740 in-state and $44,340 out-of-state. No transportation figure is listed.',
        source: 'costs',
        netPrice: {
            average: 13042,
            byIncome: [
                { income: '$0 to $30,000', amount: 871 },
                { income: '$30,001 to $48,000', amount: 2983 },
                { income: '$48,001 to $75,000', amount: 5924 },
                { income: '$75,001 to $110,000', amount: 11952 },
                { income: 'Over $110,000', amount: 29998 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from the Illinois aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
